import { useMemo, useState } from 'react';
import { ChevronRightIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import type { ExcludedTeethMap } from '../../../../../../services/dmMonitoringControl';
import { DmButton, FoldSection, SavedText, TOOLTIP_OVER_MODAL } from '../DmControlPrimitives';
import {
    notificationEntries,
    notificationLabel,
    taxonomyWith,
    type NotificationEntry
} from './excludedTeethTaxonomy';
import { previewNotifications } from './notificationPreview';
import { ToothMiniChart } from './ToothMiniChart';
import { formatTeeth } from './toothNumbering';

interface NotificationsSectionProps {
    excluded: ExcludedTeethMap | null;
    loadError: string | null;
    readOnly: boolean;
    /** True right after the excluded-teeth step saved, to confirm it here. */
    justSaved: boolean;
    onEdit: () => void;
}

// The notifications with excluded teeth, and the way into the editor step.
export function NotificationsSection({
    excluded,
    loadError,
    readOnly,
    justSaved,
    onEdit
}: NotificationsSectionProps) {
    const byReference = useMemo(() => {
        const entries = notificationEntries(taxonomyWith(Object.keys(excluded ?? {})));
        return new Map<string, NotificationEntry>(entries.map((entry) => [entry.reference, entry]));
    }, [excluded]);
    const [showAll, setShowAll] = useState(false);
    const set = Object.entries(excluded ?? {}).filter(([, teeth]) => teeth.length > 0);
    const { shown, hiddenCount } = previewNotifications(set, showAll);
    const summary = !excluded
        ? loadError
            ? `Couldn't load the excluded teeth: ${loadError}`
            : 'Loading…'
        : set.length
          ? `Excluded teeth on ${set.length} notification${set.length === 1 ? '' : 's'}`
          : 'No excluded teeth';

    return (
        <FoldSection
            icon={EyeSlashIcon}
            title="Notifications"
            meta={<span className="truncate text-[13px] text-gray-500">{summary}</span>}
            actions={
                <>
                    {justSaved && <SavedText />}
                    {!readOnly && excluded && (
                        <DmButton size="sm" onClick={onEdit}>
                            Edit excluded teeth
                            <ChevronRightIcon className="-mr-1 h-4 w-4" />
                        </DmButton>
                    )}
                </>
            }
        >
            {excluded && set.length === 0 && (
                <p className="text-[13px] text-gray-500">
                    Dental Monitoring checks every tooth for all notifications.
                </p>
            )}
            {set.length > 0 && (
                <ul className="-mt-2 divide-y divide-gray-100">
                    {shown.map(([reference, teeth]) => {
                        const entry = byReference.get(reference);
                        return (
                            <li key={reference} className="flex items-center gap-5 py-2.5">
                                <span className="min-w-0 flex-1 truncate text-[13px] text-gray-800">
                                    {notificationLabel(reference)}
                                    {entry && (
                                        <span className="text-gray-400">
                                            {' '}
                                            · {entry.category.label} › {entry.observation.label}
                                        </span>
                                    )}
                                </span>
                                {/* The numbers show on hover, for when the chart is too small to read. */}
                                <Tooltip
                                    content={`Excluded: ${formatTeeth(teeth)}`}
                                    className={TOOLTIP_OVER_MODAL}
                                    focusable
                                >
                                    <span className="shrink-0">
                                        <ToothMiniChart teeth={teeth} />
                                    </span>
                                </Tooltip>
                            </li>
                        );
                    })}
                </ul>
            )}
            {hiddenCount > 0 && (
                <button
                    type="button"
                    onClick={readOnly ? () => setShowAll(true) : onEdit}
                    className="mt-1 text-xs font-medium text-[#007E87] hover:underline"
                >
                    +{hiddenCount} more
                </button>
            )}
        </FoldSection>
    );
}
